// src/utils/downloadUtils.ts
import { AxiosResponse } from "axios"

/**
 * UTF-8 byte-order mark. Excel only detects a CSV as UTF-8 when the file starts
 * with it — without it, a double-clicked CSV is read as ANSI and any non-ASCII
 * character (en-dashes, curly quotes) renders as mojibake.
 */
export const UTF8_BOM = "\uFEFF"

/**
 * Downloads a CSV string with the UTF-8 BOM prepended so Excel opens it
 * correctly. Use this for every client-generated CSV download; the engine's own
 * CSV serializations already carry the BOM, and all CSV parse paths strip it.
 *
 * @param csvContent - The CSV content (BOM added if not already present).
 * @param filename - The desired name for the downloaded file.
 */
export const downloadCsvAsFile = (csvContent: string, filename: string): void => {
  const content = csvContent.startsWith(UTF8_BOM)
    ? csvContent
    : UTF8_BOM + csvContent
  downloadDataAsFile(content, filename, "text/csv;charset=utf-8")
}

/**
 * Initiates a browser download for the provided data.
 *
 * @param data - The data to be downloaded. Can be a string, Blob, ArrayBuffer, or TypedArray.
 * @param filename - The desired name for the downloaded file.
 * @param mimeType - The MIME type of the data (e.g., 'application/json', 'text/csv', 'image/png').
 *                   Defaults to 'application/octet-stream' if not provided.
 */
export function downloadDataAsFile(
  data: BlobPart | BlobPart[],
  filename: string,
  mimeType: string = "application/octet-stream"
): void {
  try {
    const blob = new Blob(Array.isArray(data) ? data : [data], {
      type: mimeType,
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link) // Clean up by removing the link

    window.URL.revokeObjectURL(url) // Release the object URL
  } catch (error) {
    console.error(
      `Error during file download process for "${filename}":`,
      error
    )
  }
}

/**
 * Initiates a browser download for the given content as a JSON file.
 *
 * @param content - The JavaScript object or array to be stringified and downloaded.
 * @param baseFilename - The base name for the downloaded file (e.g., "my-data").
 *                       The ".json" extension will be appended automatically if not present.
 */
export function downloadJSON(content: unknown, baseFilename: string): void {
  try {
    const jsonString = JSON.stringify(content, null, 2) // Pretty print JSON
    const filenameWithExtension = baseFilename.endsWith(".json")
      ? baseFilename
      : `${baseFilename}.json`
    downloadDataAsFile(jsonString, filenameWithExtension, "application/json")
  } catch (error) {
    console.error(
      `Error preparing JSON for download ("${baseFilename}.json"):`,
      error
    )
  }
}

/**
 * Initiates a browser download for the data provided in an Axios response.
 *
 * @param response - The AxiosResponse object. `response.data` is used as the content.
 * @param filename - The desired name for the downloaded file.
 */
export function downloadResponseAsFile(
  response: AxiosResponse<unknown>,
  filename: string
): void {
  if (response.data === undefined || response.data === null) {
    console.error(`Error downloading "${filename}": Response data is empty.`)
    return
  }

  let dataToDownload: BlobPart
  const responseDataType = typeof response.data

  if (response.data instanceof Blob) {
    dataToDownload = response.data
  } else if (
    responseDataType === "string" ||
    response.data instanceof ArrayBuffer ||
    ArrayBuffer.isView(response.data)
  ) {
    dataToDownload = response.data as BlobPart
  } else if (responseDataType === "object") {
    // If it's an object, assume it's JSON and stringify it.
    // For true binary files, Axios responseType should be 'arraybuffer' or 'blob'.
    try {
      dataToDownload = JSON.stringify(response.data)
      // Only warn if content-type isn't explicitly JSON, to avoid noise for actual JSON APIs
      if (!response.headers["content-type"]?.includes("application/json")) {
        console.warn(
          `Warning: Downloading stringified object for "${filename}". For binary files, ensure Axios responseType is "arraybuffer" or "blob".`
        )
      }
    } catch (e) {
      console.error(
        `Error downloading "${filename}": Could not stringify response data.`,
        e
      )
      return
    }
  } else {
    console.error(
      `Error downloading "${filename}": Response data type "${responseDataType}" is not directly downloadable.`
    )
    return
  }

  const contentType =
    response.headers["content-type"] || "application/octet-stream"
  downloadDataAsFile(dataToDownload, filename, contentType)
}
